import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router";

import { confirmCode, isSignedIn, safeNext, signIn } from "@/auth/api";
import type { SignInResult } from "@/auth/api";
import { AuthError, AuthNotice, AuthScreen } from "@/auth/AuthScreen";
import { Button, FormField, Input } from "@/components/ui";

/** What `PasswordReset` leaves in the router state on its way here. */
export type LoginState = { passwordChanged?: boolean } | null;

/**
 * Sign-in: the password, then the authenticator code.
 *
 * Two steps on one screen because that is what the server makes of it — the
 * second factor is owed by every account (.docs/06 section 9 item 13), and
 * allauth holds the half-finished sign-in in the session between the two.
 * An account that has not enrolled yet is signed in by the password alone and
 * sent to enrol by the first API call it makes (`client`'s 403 handling).
 */
export function Login() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const location = useLocation();
  const next = safeNext(params.get("next"));

  const [step, setStep] = useState<"password" | "code">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    (location.state as LoginState)?.passwordChanged
      ? t("auth.login.passwordChanged")
      : null,
  );

  // A bookmark of this page, opened with a session that is still good: there
  // is nothing to ask. The form is drawn meanwhile rather than held back,
  // because nearly everyone who lands here was sent for want of a session.
  useEffect(() => {
    let cancelled = false;
    void isSignedIn().then((signedIn) => {
      if (signedIn && !cancelled) window.location.assign(next);
    });
    return () => {
      cancelled = true;
    };
  }, [next]);

  function settle(result: SignInResult) {
    switch (result.step) {
      case "done":
        // A page load, not a router push: `next` may be one of Django's own
        // pages (`/admin/`), which the router cannot reach. `busy` stays set
        // so the button does not wake up while the browser is leaving.
        window.location.assign(next);
        return;
      case "code":
        setStep("code");
        setNotice(null);
        break;
      case "verifyEmail":
        setNotice(t("auth.login.verifyEmail"));
        break;
      case "expired":
        startOver();
        setError(t("auth.code.expired"));
        break;
      case "error":
        setError(result.message);
        break;
    }
    setBusy(false);
  }

  function startOver() {
    setStep("password");
    setPassword("");
    setCode("");
    setError(null);
  }

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    settle(await signIn(email.trim(), password));
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    // Authenticator apps show the code as "123 456".
    settle(await confirmCode(code.replace(/\s+/g, "")));
  }

  if (step === "code") {
    return (
      <AuthScreen
        title={t("auth.code.title")}
        footer={
          <>
            <span className="text-mute-700">{email}</span>
            <Button onClick={startOver}>{t("auth.code.back")}</Button>
          </>
        }
      >
        {error ? <AuthError>{error}</AuthError> : null}
        <form onSubmit={submitCode} className="space-y-4">
          <FormField label={t("auth.code.label")} hint={t("auth.code.hint")}>
            {(id) => (
              <Input
                id={id}
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            )}
          </FormField>
          <Button variant="primary" type="submit" block disabled={busy}>
            {busy ? t("auth.code.submitting") : t("auth.code.submit")}
          </Button>
        </form>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      title={t("auth.login.title")}
      footer={
        <>
          <span className="text-mute-700">{t("auth.login.forgot")}</span>
          {/* The address typed so far goes along, so it is not typed twice. */}
          <Link to="/recuperar" state={{ email }} className="btn btn-secondary">
            {t("auth.login.recover")}
          </Link>
        </>
      }
    >
      {notice ? <AuthNotice>{notice}</AuthNotice> : null}
      {error ? <AuthError>{error}</AuthError> : null}
      <form onSubmit={submitPassword} className="space-y-4">
        <FormField label={t("auth.login.email")}>
          {(id) => (
            <Input
              id={id}
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </FormField>
        <FormField label={t("auth.login.password")}>
          {(id) => (
            <Input
              id={id}
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </FormField>
        <Button variant="primary" type="submit" block disabled={busy}>
          {busy ? t("auth.login.submitting") : t("auth.login.submit")}
        </Button>
      </form>
    </AuthScreen>
  );
}
